/**
 * Admin Apps section: list apps, toggle status, edit metadata; user app access (grant/revoke/role).
 * 003-multi-app-platform T060–T062.
 */
import { useEffect, useState } from 'react';
import {
  adminApi,
  type AdminApp,
  type AdminAppRole,
  type AllowedUser,
  type UserAppAccessGrant,
} from '@/services/admin';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/services/api';

export function AdminAppsSection() {
  const [apps, setApps] = useState<AdminApp[]>([]);
  const [users, setUsers] = useState<AllowedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [grants, setGrants] = useState<UserAppAccessGrant[]>([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [grantAppId, setGrantAppId] = useState<string>('');
  const [granting, setGranting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [appRolesCache, setAppRolesCache] = useState<Record<string, AdminAppRole[]>>({});

  const loadApps = async () => {
    setError(null);
    try {
      const list = await adminApi.listApps();
      setApps(list);
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to load apps'));
    }
  };

  const loadUsers = async () => {
    try {
      const list = await adminApi.listUsers(true);
      setUsers(list);
    } catch {
      // non-fatal
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([loadApps(), loadUsers()]);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedUserId) {
      setGrants([]);
      return;
    }
    let cancelled = false;
    setGrantsLoading(true);
    adminApi
      .listUserAppAccess(selectedUserId)
      .then((list) => {
        if (!cancelled) setGrants(list);
      })
      .catch(() => {
        if (!cancelled) setGrants([]);
      })
      .finally(() => {
        if (!cancelled) setGrantsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUserId]);

  const handleStatusChange = async (
    app: AdminApp,
    newStatus: 'active' | 'coming_soon' | 'disabled'
  ) => {
    if (app.status === newStatus) return;
    setUpdatingStatus(app.id);
    setError(null);
    try {
      await adminApi.updateApp(app.id, {
        name: app.name,
        route_path: app.route_path,
        description: app.description ?? undefined,
        icon: app.icon ?? undefined,
        status: newStatus,
        display_order: app.display_order,
        theme_id: app.theme_id ?? undefined,
      });
      await loadApps();
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to update status'));
    } finally {
      setUpdatingStatus(null);
    }
  };

  const loadAppRoles = async (appId: string): Promise<AdminAppRole[]> => {
    if (appRolesCache[appId]) return appRolesCache[appId];
    const roles = await adminApi.listAppRoles(appId);
    setAppRolesCache((prev) => ({ ...prev, [appId]: roles }));
    return roles;
  };

  const handleGrant = async () => {
    if (!selectedUserId || !grantAppId) return;
    setGranting(true);
    setError(null);
    try {
      await adminApi.grantUserAppAccess(selectedUserId, grantAppId);
      setGrantAppId('');
      const list = await adminApi.listUserAppAccess(selectedUserId);
      setGrants(list);
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to grant access'));
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (accessId: string) => {
    if (!selectedUserId || !confirm('Revoke this app access?')) return;
    setError(null);
    try {
      await adminApi.revokeUserAppAccess(selectedUserId, accessId);
      const list = await adminApi.listUserAppAccess(selectedUserId);
      setGrants(list);
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to revoke'));
    }
  };

  const handleRoleChange = async (grant: UserAppAccessGrant, appRoleId: string) => {
    if (!selectedUserId || grant.app_role?.id === appRoleId) return;
    setError(null);
    try {
      await adminApi.patchUserAppAccess(selectedUserId, grant.id, appRoleId);
      const list = await adminApi.listUserAppAccess(selectedUserId);
      setGrants(list);
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to update role'));
    }
  };

  if (loading) {
    return (
      <section>
        <h2 className="platform-admin-heading text-lg font-semibold mb-3">Apps</h2>
        <p className="platform-admin-muted">Loading apps...</p>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <h2 className="platform-admin-heading text-lg font-semibold">Apps</h2>
      <p className="platform-admin-muted text-sm">
        App registry and status. Toggle status; manage user app access below.
      </p>
      {error && (
        <div className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
      )}
      {apps.length === 0 ? (
        <p className="platform-admin-muted">No apps in registry.</p>
      ) : (
        <ul className="border rounded-lg divide-y">
          {apps.map((app) => (
            <li key={app.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <span className="font-medium platform-admin-heading">{app.name}</span>
                <span className="ml-2 text-sm platform-admin-muted">{app.route_path}</span>
                <span
                  className={cn(
                    'ml-2 text-xs px-1.5 py-0.5 rounded',
                    app.status === 'active' && 'bg-green-500/20 text-green-700 dark:text-green-400',
                    app.status === 'coming_soon' &&
                      'bg-amber-500/20 text-amber-700 dark:text-amber-400',
                    app.status === 'disabled' && 'bg-muted text-muted-foreground'
                  )}
                >
                  {app.status}
                </span>
              </div>
              <select
                value={app.status}
                onChange={(e) =>
                  handleStatusChange(app, e.target.value as 'active' | 'coming_soon' | 'disabled')
                }
                disabled={updatingStatus === app.id}
                className="rounded-md border border-[color-mix(in srgb, var(--accent) 35%, transparent)] bg-[var(--bg-elevated)] px-2 py-1 text-sm text-[var(--fg)]"
              >
                <option value="active">active</option>
                <option value="coming_soon">coming_soon</option>
                <option value="disabled">disabled</option>
              </select>
            </li>
          ))}
        </ul>
      )}

      <div>
        <h3 className="platform-admin-heading text-base font-semibold mb-2">User app access</h3>
        <p className="platform-admin-muted text-sm mb-3">
          Select a user to view and manage their app access (grant, revoke, set per-app role).
        </p>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="space-y-1">
            <label className="platform-admin-heading text-sm font-medium">User</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="rounded-md border border-[color-mix(in srgb, var(--accent) 35%, transparent)] bg-[var(--bg-elevated)] px-3 py-1.5 text-sm min-w-[200px] text-[var(--fg)]"
            >
              <option value="">— Select user —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                  {!u.active ? ' (inactive)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        {selectedUserId && (
          <>
            {grantsLoading ? (
              <p className="platform-admin-muted text-sm">Loading access...</p>
            ) : (
              <>
                <div className="flex flex-wrap items-end gap-2 mb-3">
                  <select
                    value={grantAppId}
                    onChange={(e) => setGrantAppId(e.target.value)}
                    className="rounded-md border border-[color-mix(in srgb, var(--accent) 35%, transparent)] bg-[var(--bg-elevated)] px-3 py-1.5 text-sm min-w-[180px] text-[var(--fg)]"
                  >
                    <option value="">— Select app to grant —</option>
                    {apps
                      .filter((a) => !grants.some((g) => g.app_id === a.id))
                      .map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleGrant}
                    disabled={granting || !grantAppId}
                  >
                    Grant access
                  </Button>
                </div>
                {grants.length === 0 ? (
                  <p className="platform-admin-muted text-sm">No app access. Grant access above.</p>
                ) : (
                  <ul className="border rounded-lg divide-y">
                    {grants.map((g) => (
                      <li key={g.id} className="flex items-center justify-between px-4 py-3">
                        <div>
                          <span className="font-medium platform-admin-heading">
                            {g.app_name ?? g.app_id}
                          </span>
                          <span className="ml-2 text-sm platform-admin-muted">
                            {g.app_role ? g.app_role.name : '—'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <GrantRoleSelect
                            grant={g}
                            onRoleChange={(roleId) => handleRoleChange(g, roleId)}
                            loadAppRoles={loadAppRoles}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevoke(g.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            Revoke
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function GrantRoleSelect({
  grant,
  onRoleChange,
  loadAppRoles,
}: {
  grant: UserAppAccessGrant;
  onRoleChange: (appRoleId: string) => void;
  loadAppRoles: (appId: string) => Promise<AdminAppRole[]>;
}) {
  const [roles, setRoles] = useState<AdminAppRole[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadAppRoles(grant.app_id)
      .then((r) => {
        if (!cancelled) setRoles(r);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [grant.app_id, loadAppRoles]);
  if (loading || roles.length === 0) return <span className="text-sm platform-admin-muted">—</span>;
  return (
    <select
      value={grant.app_role?.id ?? ''}
      onChange={(e) => onRoleChange(e.target.value)}
      className="rounded-md border border-[color-mix(in srgb, var(--accent) 35%, transparent)] bg-[var(--bg-elevated)] px-2 py-1 text-sm max-w-[120px] text-[var(--fg)]"
    >
      {roles.map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}
        </option>
      ))}
    </select>
  );
}
